import React, { useState } from 'react';
import ExerciseDetailModal from './ExerciseDetailModal';
import { isTimeType, isCardioType, usesWeight } from '../lib/exerciseType';
import './ExerciseExecutionCard.css';

function ExerciseExecutionCard({ exercise, setStates, onToggleSet, onExpandSet, onUpdateSet, onToggleAllSets, onAddExtraSet, onDeleteSet, onStartRestTimer, isExtraExercise, lastPerformed, firstPerformed, suggestedWeight, isExpanded = true, onToggleExpand, showDeleteAll = false }) {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(null); // Track which set has notes expanded

  if (!exercise || !setStates) {
    return null;
  }

  // Detect exercise type (dal catalogo pulito: reps/time/hold/cardio/distance)
  const isCardio = isCardioType(exercise);
  const isTimeBased = isTimeType(exercise.exercise_type);
  const showWeight = usesWeight(exercise);

  // Calcola se tutte le serie sono completate
  const allCompleted = setStates.every(s => s.completed);
  const someCompleted = setStates.some(s => s.completed);
  const completedCount = setStates.filter(s => s.completed).length;

  const handleExerciseNameClick = (e) => {
    e.stopPropagation();
    setShowDetailModal(true);
  };

  // Calcola se tutte le serie "base" sono completate (condizione per mostrare
  // "+ Serie extra"). Per un esercizio prescritto le base sono le non-extra;
  // per un esercizio EXTRA tutte le serie sono isExtra, quindi le base sono
  // quelle iniziali (extraType 'extra_exercise'), escluse le serie aggiunte dopo.
  const prescribedSets = isExtraExercise
    ? setStates.filter(s => s.extraType !== 'extra_set')
    : setStates.filter(s => !s.isExtra);
  const allPrescribedCompleted = prescribedSets.length > 0 && prescribedSets.every(s => s.completed);

  const handleMasterToggle = (e) => {
    e.stopPropagation();
    const newCompletedState = !allCompleted;
    if (onToggleAllSets) {
      // Aggiornamento ATOMICO di tutte le serie (un solo setState nel parent,
      // un solo timer di recupero). Il loop per-serie sottostante è il fallback
      // legacy: con setState non funzionali le chiamate batchate si
      // sovrascrivono e resta completata solo l'ultima serie.
      onToggleAllSets(newCompletedState);
      return;
    }
    setStates.forEach(setState => {
      if (setState.completed !== newCompletedState) {
        onToggleSet(setState.setNumber);
      }
    });
  };

  const toggleNotes = (setNumber) => {
    setExpandedNotes(expandedNotes === setNumber ? null : setNumber);
  };

  const handleCardClick = () => {
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  return (
    <>
      <div
        className={`exercise-card ${isExtraExercise ? 'exercise-card--extra' : ''} ${allCompleted ? 'exercise-card--completed' : ''} ${!isExpanded ? 'exercise-card--collapsed' : ''}`}
        onClick={!isExpanded ? handleCardClick : undefined}
      >
        {/* Header */}
        <div className="exercise-card__header" onClick={isExpanded && onToggleExpand ? handleCardClick : undefined}>
          <label className="exercise-card__master-check" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={allCompleted}
              onChange={handleMasterToggle}
              ref={input => {
                if (input) {
                  input.indeterminate = someCompleted && !allCompleted;
                }
              }}
            />
            <span className="exercise-card__checkmark exercise-card__checkmark--large">
              {allCompleted && '✓'}
              {someCompleted && !allCompleted && '−'}
            </span>
          </label>

          <div className="exercise-card__title-area">
            <h3
              className="exercise-card__name"
              onClick={handleExerciseNameClick}
            >
              {exercise.name}
              {isExtraExercise && <span className="exercise-card__extra-badge">EXTRA</span>}
            </h3>
            {isExpanded ? (
              <>
                <div className="exercise-card__meta">
                  {lastPerformed && lastPerformed.weight && (
                    <span className="exercise-card__starting-weight">
                      Ultimo peso: {lastPerformed.weight}kg
                    </span>
                  )}
                  {lastPerformed && lastPerformed.repsArray && lastPerformed.repsArray.length > 0 && (
                    <span className="exercise-card__last-reps">
                      Ultime reps: {lastPerformed.repsArray.join(' + ')}
                    </span>
                  )}
                  {suggestedWeight && (
                    <span className="exercise-card__suggestion">
                      💡 {suggestedWeight}
                    </span>
                  )}
                </div>
                {lastPerformed && lastPerformed.notesArray && lastPerformed.notesArray.length > 0 && (
                  <div className="exercise-card__last-notes">
                    <span className="exercise-card__last-notes-label">💡 Ultima nota:</span>
                    <span className="exercise-card__last-notes-text">
                      {lastPerformed.notesArray[0]}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="exercise-card__collapsed-meta">
                {isCardio ? (
                  <span className="exercise-card__progress-text">
                    {allCompleted ? '✓ Completato' : `${exercise.duration_minutes} min`}
                  </span>
                ) : (
                  <>
                    <span className="exercise-card__progress-text">
                      {completedCount}/{setStates.length} serie
                    </span>
                    {exercise.rest_seconds && (
                      <span className="exercise-card__rest-text">• {exercise.rest_seconds}s rec</span>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="exercise-card__right">
            <span className="exercise-card__target">
              {isCardio ? (
                <>
                  {exercise.duration_minutes} min
                  {exercise.target_speed && ` · ${exercise.target_speed}km/h`}
                </>
              ) : isTimeBased ? (
                <>
                  {exercise.sets}×{exercise.reps}s
                  {exercise.weight && ` @${exercise.weight}kg`}
                </>
              ) : (
                <>
                  {exercise.sets}×{exercise.reps}
                  {exercise.weight && ` @${exercise.weight}kg`}
                </>
              )}
            </span>
            {onToggleExpand && (
              <span className={`exercise-card__chevron ${isExpanded ? 'exercise-card__chevron--open' : ''}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            )}
          </div>
        </div>

        {isExpanded && exercise.notes && (
          <p className="exercise-card__notes">{exercise.notes}</p>
        )}

        {/* Sets - Compact inline rows - only show when expanded */}
        {isExpanded && (
          <div className="exercise-card__sets">
            {isCardio ? (
              // Cardio: single row with duration/speed/incline/distance
              <div className={`set-row set-row--cardio ${setStates[0]?.completed ? 'set-row--completed' : ''}`}>
                <label className="set-row__check">
                  <input
                    type="checkbox"
                    checked={setStates[0]?.completed || false}
                    onChange={() => onToggleSet(1)}
                  />
                  <span className="set-row__checkmark">
                    {setStates[0]?.completed && '✓'}
                  </span>
                </label>

                <div className="set-row__cardio-inputs">
                  <div className="set-row__cardio-field">
                    <span className="set-row__cardio-label">Durata</span>
                    <div className="set-row__cardio-input-wrap">
                      <input
                        type="number"
                        value={setStates[0]?.duration || ''}
                        onChange={(e) => onUpdateSet(1, 'duration', e.target.value)}
                        placeholder={exercise.duration_minutes?.toString() || '30'}
                        min="0"
                        className="set-row__input"
                      />
                      <span className="set-row__unit">min</span>
                    </div>
                  </div>
                  <div className="set-row__cardio-field">
                    <span className="set-row__cardio-label">Distanza</span>
                    <div className="set-row__cardio-input-wrap">
                      <input
                        type="number"
                        value={setStates[0]?.distance || ''}
                        onChange={(e) => onUpdateSet(1, 'distance', e.target.value)}
                        placeholder={exercise.target_distance?.toString() || '—'}
                        min="0"
                        step="0.1"
                        className="set-row__input"
                      />
                      <span className="set-row__unit">km</span>
                    </div>
                  </div>
                  <div className="set-row__cardio-field">
                    <span className="set-row__cardio-label">Velocità</span>
                    <div className="set-row__cardio-input-wrap">
                      <input
                        type="number"
                        value={setStates[0]?.speed || ''}
                        onChange={(e) => onUpdateSet(1, 'speed', e.target.value)}
                        placeholder={exercise.target_speed?.toString() || '—'}
                        min="0"
                        step="0.5"
                        className="set-row__input"
                      />
                      <span className="set-row__unit">km/h</span>
                    </div>
                  </div>
                  <div className="set-row__cardio-field">
                    <span className="set-row__cardio-label">Inclinaz.</span>
                    <div className="set-row__cardio-input-wrap">
                      <input
                        type="number"
                        value={setStates[0]?.incline || ''}
                        onChange={(e) => onUpdateSet(1, 'incline', e.target.value)}
                        placeholder={exercise.target_incline?.toString() || '—'}
                        min="0"
                        max="30"
                        step="0.5"
                        className="set-row__input"
                      />
                      <span className="set-row__unit">%</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => toggleNotes(1)}
                  className={`set-row__note-btn ${expandedNotes === 1 ? 'set-row__note-btn--active' : ''} ${setStates[0]?.notes ? 'set-row__note-btn--has-notes' : ''}`}
                  aria-label="Note"
                >
                  {setStates[0]?.notes ? '📝' : '+'}
                </button>
              </div>
            ) : (
              // Strength: multiple sets with reps/weight
              setStates.map((setState) => (
                <div
                  key={setState.setNumber}
                  className={`set-row ${setState.completed ? 'set-row--completed' : ''} ${setState.isExtra ? 'set-row--extra' : ''}`}
                >
                  <label className="set-row__check">
                    <input
                      type="checkbox"
                      checked={setState.completed}
                      onChange={() => onToggleSet(setState.setNumber)}
                    />
                    <span className="set-row__checkmark">
                      {setState.completed && '✓'}
                    </span>
                  </label>

                  <span className="set-row__label">
                    {setState.setNumber}
                    {setState.isExtra && <span className="set-row__extra-dot"></span>}
                  </span>

                  <div className="set-row__inputs">
                    {isTimeBased ? (
                      <>
                        <input
                          type="number"
                          value={setState.duration || ''}
                          onChange={(e) => onUpdateSet(setState.setNumber, 'duration', e.target.value)}
                          placeholder={exercise.reps}
                          min="0"
                          className="set-row__input set-row__input--reps"
                        />
                        <span className="set-row__unit">s</span>
                        {showWeight && exercise.weight && (
                          <>
                            <span className="set-row__unit">×</span>
                            <input
                              type="number"
                              value={setState.weight}
                              onChange={(e) => onUpdateSet(setState.setNumber, 'weight', e.target.value)}
                              placeholder={exercise.weight || '—'}
                              min="0"
                              step="0.5"
                              className="set-row__input set-row__input--weight"
                            />
                            <span className="set-row__unit">kg</span>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <input
                          type="number"
                          value={setState.reps}
                          onChange={(e) => onUpdateSet(setState.setNumber, 'reps', e.target.value)}
                          placeholder={exercise.reps}
                          min="0"
                          className="set-row__input set-row__input--reps"
                        />
                        {showWeight ? (
                          <>
                            <span className="set-row__unit">×</span>
                            <input
                              type="number"
                              value={setState.weight}
                              onChange={(e) => onUpdateSet(setState.setNumber, 'weight', e.target.value)}
                              placeholder={exercise.weight || '—'}
                              min="0"
                              step="0.5"
                              className="set-row__input set-row__input--weight"
                            />
                            <span className="set-row__unit">kg</span>
                            {setState.setNumber === 1 && (
                              <span className="set-row__weight-hint">tot.</span>
                            )}
                          </>
                        ) : (
                          <span className="set-row__unit">reps</span>
                        )}
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => toggleNotes(setState.setNumber)}
                    className={`set-row__note-btn ${expandedNotes === setState.setNumber ? 'set-row__note-btn--active' : ''} ${setState.notes ? 'set-row__note-btn--has-notes' : ''}`}
                    aria-label="Note"
                  >
                    {setState.notes ? '📝' : '+'}
                  </button>

                  {/* Delete: show for extra sets, multiple sets, or when showDeleteAll (free workout) */}
                  {(showDeleteAll || setState.isExtra || setStates.length > 1) && onDeleteSet && (
                    <button
                      onClick={() => onDeleteSet(setState.setNumber)}
                      className="set-row__delete-btn"
                      aria-label="Elimina serie"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))
            )}

            {/* Inline notes area */}
            {expandedNotes && (
              <div className="set-row__notes-area">
                <textarea
                  value={setStates.find(s => s.setNumber === expandedNotes)?.notes || ''}
                  onChange={(e) => onUpdateSet(expandedNotes, 'notes', e.target.value)}
                  placeholder="Note per questa serie..."
                  rows="2"
                  className="set-row__notes-input"
                  autoFocus
                />
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Non inserire informazioni mediche, diagnosi o dati sanitari.</p>
              </div>
            )}
          </div>
        )}

        {/* Add extra set button - subtle - only show when expanded (not for cardio) */}
        {isExpanded && !isCardio && allPrescribedCompleted && onAddExtraSet && (
          <button
            onClick={onAddExtraSet}
            className="exercise-card__add-set"
          >
            + Serie extra
          </button>
        )}
      </div>

      {/* Modal dettagli esercizio */}
      {showDetailModal && (
        <ExerciseDetailModal
          exercise={exercise}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </>
  );
}

export default ExerciseExecutionCard;
